/**
 * API Endpoints
 *
 * Centralized API endpoint definitions.
 * All API endpoints should be defined here for consistency and maintainability.
 */
import { CB_BACKEND_URL } from './config';

const API_BASE = `${CB_BACKEND_URL}/api`;

// ============================================
// Session Endpoints
// ============================================

export const SESSION_ENDPOINTS = {
  INIT: `${API_BASE}/session/init`,
} as const;

// ============================================
// Chat Endpoints
// ============================================

export const CHAT_ENDPOINTS = {
  SEND_MESSAGE: `${API_BASE}/resources/v1/chat`,
  /** Chat stream endpoint - SSE with THINKING, FINAL_RESPONSE, STREAM_END events */
  SEND_MESSAGE_STREAM: `${API_BASE}/resources/v1/chatstream`,
  /** Fast summary endpoint - returns messages + pagination for the left panel */
  CHAT_SUMMARY: `${API_BASE}/resources/v1/chat/summary`,
  /** Async template details endpoint - returns activeTemplate + templateData for the right panel */
  CHAT_TEMPLATE_DETAILS: `${API_BASE}/resources/v1/chat/template-details`,
  POLL_RESULT: `${API_BASE}/flights/search/poll`,
  /** Paginated list of user's past chat sessions for sidebar */
  CHATS_LIST: `${API_BASE}/resources/v1/chats`,
} as const;

// ============================================
// Flights Endpoints
// ============================================

export const FLIGHTS_ENDPOINTS = {
  AUTOSUGGEST: `${API_BASE}/flights/autosuggest`,
  FARE_CALENDAR: `${API_BASE}/flights/fare-calendar`,
  ADDONS_ANCILLARY: `${API_BASE}/flights/addons/ancillary`,
  ADDON_SELECTION: `${API_BASE}/flights/addon-selection`,
  MEALS: `${API_BASE}/flights/addons/meal`,
  BAGGAGE: `${API_BASE}/flights/addons/baggage`,
  SEATS: `${API_BASE}/flights/addons/seats`,
  COUPONS: `${API_BASE}/flights/coupon`,
  REVIEW: `${API_BASE}/flights/interstitial`,
  PREBOOK: `${API_BASE}/flights/prebook`,
  REVIEW_TRAVELLER: `${API_BASE}/flights/review-traveller-form`,
  VALIDATE_TRAVELLER_FORM: `${API_BASE}/flights/validate-traveller-form`,
  VALIDATE_TRAVELLER_INFO: `${API_BASE}/flights/validate-traveller-info`,
  FLIGHT_DETAILS: `${API_BASE}/flights/flight-details`,
} as const;

// ============================================
// Hotels Endpoints
// ============================================

export const HOTELS_ENDPOINTS = {
  DETAILS: `${API_BASE}/hotels/details`,
  RATINGS_AND_REVIEWS_SUMMARY: `${API_BASE}/hotels/ratings-reviews/summary`,
  RATINGS_AND_REVIEWS_LIST: `${API_BASE}/hotels/ratings-reviews/list`,
  BOOKING_REVIEW: `${API_BASE}/hotels/booking-review`,
  POLICY_RULES: `${API_BASE}/hotels/policy-rules`,
  /** Traveller add/edit/select/deselect operations via Agent API */
  TRAVELLER_DETAILS: `${API_BASE}/hotels/traveller-details`,
  /** GST add/edit/remove operations via Agent API */
  GST_DETAILS: `${API_BASE}/hotels/gst-details`,
  /** PAN add/remove operations via Agent API */
  PAN_DETAILS: `${API_BASE}/hotels/pan-details`,
  COUNTRY_CODES: `${API_BASE}/hotels/country-codes`,
  CURRENCY_CONFIG: `${API_BASE}/hotels/currency-config`,
  VALIDATE_COUPON: `${API_BASE}/hotels/validate-coupon`,
  UPDATE_PRICE: `${API_BASE}/hotels/update-price`,
  OCCUPANCY_UPDATE_PRICE: `${API_BASE}/hotels/occupancy-update-price`,
  CHECKOUT: `${API_BASE}/hotels/checkout`,
  THANK_YOU: `${API_BASE}/hotels/thank-you`,
  FILTER_COUNT: `${API_BASE}/hotels/filter-count`,
} as const;

// ============================================
// Onboarding Endpoints
// ============================================

export const ONBOARDING_ENDPOINTS = {
  CONFIG: `${API_BASE}/onboarding/config`,
  AUTO_SUGGEST: `${API_BASE}/onboarding/auto-suggest`,
  PREFERENCES: `${API_BASE}/onboarding/preferences`,
  GENERATE_TRIP_QUERY: `${API_BASE}/onboarding/generate-trip-query`,
} as const;
